import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar,
  LineChart, Line,
  XAxis, YAxis,
  Tooltip, ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { FaEdit } from 'react-icons/fa';
import { useMsal } from '@azure/msal-react';

import JournalEditModal from '../../components/VisitDetails/JournalEditModal';
import { buildJournalFormData } from '../../utils/buildJournalFormData';
import { getApiBaseUrl, getAuthConfig } from '../../services/configService';
import { UserContext } from '../../contexts/UserContext';
import { FaArrowLeft } from 'react-icons/fa';

import './FFCReport.css';

const PAGE_SIZE = 12;

/* =========================================================
   MAIN COMPONENT
   ========================================================= */

const FFCReport = () => {
  const { instance, accounts } = useMsal();
  const { user } = useContext(UserContext);
  const authConfig = getAuthConfig();
  const navigate = useNavigate();
  const [startDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );

  const [reportData, setReportData] = useState(null);
  const [journalTable, setJournalTable] = useState([]);
  const [journalTotal, setJournalTotal] = useState(0);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(true);
  const [openJournal, setOpenJournal] = useState(false);
  const [journalState, setJournalState] = useState(null);

  const toggles = [
    { key: 'is_escalated', short: 'ESC', full: 'Escalated' },
    { key: 'is_pricing', short: 'PRI', full: 'Pricing Issue' },
    { key: 'is_placeholder', short: 'PH', full: 'Placeholder' },
    { key: 'is_no_error', short: 'NE', full: 'No Error' },
    { key: 'is_local_leadership', short: 'LL', full: 'Local Leadership' },
  ];

  const exportJournalsToCsv = (rows) => {
    if (!rows.length) return;
  
    const headers = [
      'Entry Date',
      'Hospital',
      'Visit ID',
      'Patient ID',
      'Charge Type',
      'Item ID',
      'Label',
      'Expected Charge',
      'Adjusted Charge',
      'Reason',
      'Note',
      'Staff ID',
      'Escalated',
      'Pricing',
      'No Error',
      'Local Leadership',
    ];
  
    const csvRows = [
      headers.join(','),
  
      ...rows.map(r => [
        `"${new Date(r.entry_date).toLocaleDateString()}"`,
        `"${r.bp_hospital}"`,
        `"${r.unique_id}"`,
        `"${r.patient_id}"`,
        `"${r.charge_type}"`,
        `"${r.invoiceitemid ?? ''}"`,
        `"${r.label ?? ''}"`,
        r.expected_charge ?? '',
        r.adjusted_charge ?? '',
        `"${r.reason ?? ''}"`,
        `"${(r.note ?? '').replace(/"/g, '""')}"`,
        `"${r.staff_id ?? ''}"`,
        r.is_escalated ? 'Yes' : 'No',
        r.is_pricing ? 'Yes' : 'No',
        r.is_no_error ? 'Yes' : 'No',
        r.is_local_leadership ? 'Yes' : 'No',
      ].join(','))
    ];
  
    const blob = new Blob([csvRows.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
  
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
  
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `journal_export_${new Date().toISOString().slice(0,10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const buildEditJournalState = (journal) => {
    return {
      journalType: 'edit',
      formData: {
        ...journal,
      },
      displayedFields: new Set([
        'invoiceitemid',
        'charge_type',
        'label',
        'completed_date',
        'entry_date',
        'bp_hospital',
        'unique_id',
        'expected_charge',
        'adjusted_charge',
        'reason',
        'note',
        'staff_id',
        'is_escalated',
        'is_pricing',
        'is_no_error',
        'is_placeholder',
        'is_local_leadership',
      ]),
      readonlyFields: new Set([
        'invoiceitemid',
        'charge_type',
        'label',
        'completed_date',
        'entry_date',
        'bp_hospital',
        'unique_id',
      ]),
    };
  };

  /* -------------------- Auth Helper -------------------- */
  const getApiAccessToken = async () => {
    if (!accounts.length) return null;
    try {
      const res = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return res.accessToken;
    } catch {
      const res = await instance.acquireTokenPopup({
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return res.accessToken;
    }
  };

  /* -------------------- Fetch Analytics -------------------- */
  const fetchFfcReport = async () => {
    setLoading(true);
    const token = await getApiAccessToken();
    if (!token) return;

    const res = await fetch(
      `${getApiBaseUrl()}/ffc-report?start_date=${startDate}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const json = await res.json();
    setReportData(json);
    setLoading(false);
  };

  /* -------------------- Fetch Journals (Table only) -------------------- */
  const fetchJournalTable = async () => {
    const token = await getApiAccessToken();
    if (!token) return;

    const res = await fetch(
      `${getApiBaseUrl()}/journal?days=14&end_date=${endDate}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const json = await res.json();
    setJournalTable(json.entries || []);
    setJournalTotal(json.total || 0);
    setPage(1);
  };

  useEffect(() => {
    fetchFfcReport();
  }, [startDate]);

  useEffect(() => {
    fetchJournalTable();
  }, [endDate]);

  /* -------------------- Derived -------------------- */
  const filteredRows = useMemo(() => {
    if (!search) return journalTable;
    const s = search.toLowerCase();
    return journalTable.filter(r =>
      Object.values(r).some(v =>
        String(v).toLowerCase().includes(s)
      )
    );
  }, [search, journalTable]);

  const pagedRows = filteredRows.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  /* -------------------- Edit -------------------- */
  const handleEditJournal = (journal) => {
    const built = buildEditJournalState(journal);
    setJournalState(built);
    setOpenJournal(true);
  };

  if (loading || !reportData) {
    return <p className="ffc-loading">Loading Financial Recovery…</p>;
  }

  const { kpis, trends, rankings } = reportData;

  const truncate = (text, length = 12) => {
    if (!text) return '';
    return text.length > length
      ? `${text.slice(0, length)}…`
      : text;
  };
  
  return (
    <div className="ffc-report">

      {/* ================= HEADER ================= */}
      <div className="report-header-wrapper">
        <button
          className="back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
          title="Back"
        >
          <FaArrowLeft />
        </button>

        <div className="ffc-report-header">
          <div>
            <div className="ffc-report-title">Financial Recovery Overview</div>
            <div className="ffc-report-subtitle">
              Performance, revenue recovery, and documentation quality
            </div>
          </div>
        </div>
      </div>

      {/* ================= KPIs ================= */}
      <div className="ffc-kpi-block">
        {[
          ['Today', kpis.today, 'ffc-kpi-blue'],
          ['Week‑to‑Date', kpis.week_to_date, 'ffc-kpi-teal'],
          ['Period‑to‑Date', kpis.period_to_date, 'ffc-kpi-purple'],
        ].map(([label, data, cls]) => (
          <div key={label} className={`ffc-kpi-card ${cls}`}>
            <div className="ffc-kpi-label">{label}</div>
            <div className="ffc-kpi-row"><span>Journals</span><strong>{data.journal_count}</strong></div>
            <div className="ffc-kpi-row"><span>Visits</span><strong>{data.visit_count}</strong></div>
            <div className="ffc-kpi-row ffc-kpi-revenue">
              <span>Recovered</span>
              <strong>${data.recovered_revenue.toLocaleString()}</strong>
            </div>
          </div>
        ))}
      </div>

      {/* ================= CHARTS ================= */}
      <div className="chart-row">
        <div className="chart-card accent-blue">
          <h3>Recovered Revenue by Week</h3>
          <ResponsiveContainer height={260}>
            <BarChart data={trends.weekly}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week_label" tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
              <Tooltip />
              <Bar dataKey="recovered_revenue" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card accent-teal">
          <h3>Journal Volume by Week</h3>
          <ResponsiveContainer height={260}>
            <LineChart data={trends.weekly} >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week_label" tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
              <Tooltip />
              <Line dataKey="journal_count" stroke="#14b8a6" strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ================= RANKINGS ================= */}
      <div className="rank-grid">
        {[
          ['Charge Types (Period to Date)', rankings.charge_types, 'name'],
          ['Labels (Period to Date)', rankings.labels, 'name'],
          ['Hospitals (Period to Date)', rankings.hospitals, 'bp_hospital'],
        ].map(([title, data, key], i) => (
          <div key={title} className={`rank-card accent-${i}`}>
            <h3>{title}</h3>
            <div className="rank-scroll">
              {data.map((r, idx) => (
                <div key={idx} className="rank-row">
                  <span>{r[key]}</span>
                  <span>{r.journal_count} • ${r.recovered_revenue.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* ================= WORKBENCH ================= */}
      <div className="workbench">
        <div className="workbench-header">
          <div className="workbench-controls">
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
            />

            <input
              type="text"
              placeholder="Search journals…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <button
            className="export-btn"
            onClick={() => exportJournalsToCsv(filteredRows)}
            disabled={!filteredRows.length}
          >
            Export CSV
          </button>
        </div>

        <table>
          <thead>
            <tr>
              <th>Entry Date</th><th>Hospital</th><th>Patient</th><th>Type</th><th>Item ID</th>
              <th>Label</th><th>Recovered</th><th>Reason</th><th>Note</th><th>Toggles</th><th />
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((r, idx) => (
              <tr key={r.id} className={idx % 2 === 0 ? 'row-even' : 'row-odd'}>
                <td>{new Date(r.entry_date).toLocaleDateString()}</td>
                <td>{r.bp_hospital}</td>
                <td>{r.patient_id}</td>
                <td>{r.charge_type}</td>
                <td>{r.invoiceitemid}</td>
                <td>{r.label}</td>
                <td>${r.adjusted_charge}</td>
                <td>{r.reason}</td>
                <td>
                  {r.note ? (
                    <span
                      className="note-preview"
                      title={r.note}  
                    >
                      {truncate(r.note, 12)}
                    </span>
                  ) : (
                    <span className="note-empty">—</span>
                  )}
                </td>    
                <td>
                  <div className="toggle-badges">
                    {toggles.map(({ key, short, full }) => {
                      const active = !!r[key];

                      return (
                        <span
                          key={key}
                          className={`toggle-badge ${active ? 'on' : 'off'}`}
                          title={full}
                        >
                          {short}
                        </span>
                      );
                    })}
                  </div>
                </td>
                <td>
                  <button className="edit-btn" onClick={() => handleEditJournal(r)}>
                    <FaEdit />
                  </button>
                </td>  
              </tr>
            ))}
          </tbody>
        </table>

        <div className="pagination">
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
          <span>Page {page}</span>
          <button
            disabled={page * PAGE_SIZE >= filteredRows.length}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </button>
        </div>
      </div>

      <JournalEditModal
        open={openJournal}
        onClose={() => setOpenJournal(false)}
        journal={journalState}
        onSuccess={(updatedJournal) => {
          setJournalTable(prev =>
            prev.map(j =>
              j.id === updatedJournal.id ? { ...j, ...updatedJournal } : j
            )
          );
          setOpenJournal(false);
          fetchJournalTable();
          fetchFfcReport();
        }}
      />
    </div>
  );
};

export default FFCReport;